import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { ClientApi } from "../../../apis";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  clientNickname: Yup.string().required("Client nickname is required"),
  company: Yup.string().required("Company is required"),
  contactNumber: Yup.string(),
  contactPerson: Yup.string().required("Contact person is required"),
  contactTitle: Yup.string(),
  contactAddress: Yup.string(),
  phone: Yup.string()
    .matches(/^[\d\s\-\+\(\)]+$/, "Invalid phone number format")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  billingAddress: Yup.string().required("Billing address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zipCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, "Invalid zip code format")
    .required("Zip code is required"),
  billingCity: Yup.string().required("Billing city is required"),
  billingState: Yup.string().required("Billing state is required"),
  billingZipCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, "Invalid zip code format")
    .required("Billing zip code is required"),
  accountsPayableEmail: Yup.string()
    .email("Invalid email")
    .required("Accounts payable email is required"),
  notes: Yup.string(),
});

const baseInitialValues = {
  clientNickname: "",
  company: "",
  contactNumber: "",
  contactPerson: "",
  contactTitle: "",
  contactAddress: "",
  phone: "",
  email: "",
  billingAddress: "",
  city: "",
  state: "",
  zipCode: "",
  billingCity: "",
  billingState: "",
  billingZipCode: "",
  accountsPayableEmail: "",
  notes: "",
};

function ClientDetails() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientData, setClientData] = useState(baseInitialValues);
  const [projects, setProjects] = useState([]);

  const loadClient = async () => {
    setLoading(true);
    try {
      const res = await ClientApi.getClientById(clientId);
      const payload = res?.data?.data?.client || res?.data?.data || res?.data || {};
      setClientData({
        clientNickname: payload.clientNickname || "",
        company: payload.company || "",
        contactNumber: payload.contactNumber || payload.contactName || "",
        contactPerson: payload.contactPerson || "",
        contactTitle: payload.contactTitle || "",
        contactAddress: payload.contactAddress || "",
        phone: payload.phone || "",
        email: payload.email || "",
        billingAddress: payload.billingAddress || "",
        billingCity: payload.billingCity || "",
        billingState: payload.billingState || "",
        billingZipCode: payload.billingZipCode || "",
        accountsPayableEmail: payload.accountsPayableEmail || "",
        notes: payload.notes || "",
      });
      const fetchedProjects = payload.projects || payload.projectList || [];
      setProjects(Array.isArray(fetchedProjects) ? fetchedProjects : []);
    } catch (error) {
      console.error("Error fetching client:", error);
      toast.error("Failed to load client");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClient();
  }, [clientId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: clientData,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSaving(true);
        await ClientApi.updateClient(clientId, values);
        toast.success("Client updated successfully");
        await loadClient();
      } catch (error) {
        console.error("Error updating client:", error);
        toast.error(error.response?.data?.message || "Update failed");
      } finally {
        setSaving(false);
      }
    },
  });

  const formattedProjects = useMemo(() => {
    return projects.map((project) => ({
      id: project.id,
      name: project.name || project.projectName || "Untitled",
      description: project.description || "-",
      contractAmount: project.totalContractAmount || 0,

    }));
  }, [projects]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          Client Details
        </Typography>
        <Button variant="text" onClick={() => navigate("/admin/clients")}>
          Back to Clients
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Client Information
        </Typography>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Client Nickname */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Client Nickname
              </Typography>
              <TextField
                fullWidth
                id="clientNickname"
                name="clientNickname"
                placeholder="Enter client nickname"
                value={formik.values.clientNickname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.clientNickname && Boolean(formik.errors.clientNickname)}
                helperText={formik.touched.clientNickname && formik.errors.clientNickname}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* CONTACT Section */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
                CONTACT
              </Typography>
            </Grid>

            {/* Contact Person & Contact Title */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Person
              </Typography>
              <TextField
                fullWidth
                id="contactPerson"
                name="contactPerson"
                placeholder="Enter contact person"
                value={formik.values.contactPerson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.contactPerson && Boolean(formik.errors.contactPerson)}
                helperText={formik.touched.contactPerson && formik.errors.contactPerson}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Title
              </Typography>
              <TextField
                fullWidth
                id="contactTitle"
                name="contactTitle"
                placeholder="Enter contact title"
                value={formik.values.contactTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.contactTitle && Boolean(formik.errors.contactTitle)}
                helperText={formik.touched.contactTitle && formik.errors.contactTitle}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* Contact Email & Contact Phone */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Email
              </Typography>
              <TextField
                fullWidth
                id="email"
                name="email"
                placeholder="Enter contact email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Phone
              </Typography>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                placeholder="Enter contact phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* Contact Address & City */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Contact Address
              </Typography>
              <TextField
                fullWidth
                id="contactAddress"
                name="contactAddress"
                placeholder="Enter contact address"
                value={formik.values.contactAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.contactAddress && Boolean(formik.errors.contactAddress)}
                helperText={formik.touched.contactAddress && formik.errors.contactAddress}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                City
              </Typography>
              <TextField
                fullWidth
                id="city"
                name="city"
                placeholder="Enter city"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* State & Zip Code */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                State
              </Typography>
              <TextField
                fullWidth
                id="state"
                name="state"
                placeholder="Enter state"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Zip Code
              </Typography>
              <TextField
                fullWidth
                id="zipCode"
                name="zipCode"
                placeholder="Enter zip code"
                value={formik.values.zipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
                helperText={formik.touched.zipCode && formik.errors.zipCode}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* BILLING Section */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
                BILLING
              </Typography>
            </Grid>

            {/* Company Name & Accounts Payable Email */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Company Name
              </Typography>
              <TextField
                fullWidth
                id="company"
                name="company"
                placeholder="Enter company name"
                value={formik.values.company}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Accounts Payable Email
              </Typography>
              <TextField
                fullWidth
                id="accountsPayableEmail"
                name="accountsPayableEmail"
                placeholder="Enter AP email"
                value={formik.values.accountsPayableEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.accountsPayableEmail && Boolean(formik.errors.accountsPayableEmail)}
                helperText={formik.touched.accountsPayableEmail && formik.errors.accountsPayableEmail}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* Billing Address & Billing City */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing Address
              </Typography>
              <TextField
                fullWidth
                id="billingAddress"
                name="billingAddress"
                placeholder="Enter billing address"
                value={formik.values.billingAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingAddress && Boolean(formik.errors.billingAddress)}
                helperText={formik.touched.billingAddress && formik.errors.billingAddress}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing City
              </Typography>
              <TextField
                fullWidth
                id="billingCity"
                name="billingCity"
                placeholder="Enter billing city"
                value={formik.values.billingCity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingCity && Boolean(formik.errors.billingCity)}
                helperText={formik.touched.billingCity && formik.errors.billingCity}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* Billing State & Billing Zip Code */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing State
              </Typography>
              <TextField
                fullWidth
                id="billingState"
                name="billingState"
                placeholder="Enter billing state"
                value={formik.values.billingState}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingState && Boolean(formik.errors.billingState)}
                helperText={formik.touched.billingState && formik.errors.billingState}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Billing Zip Code
              </Typography>
              <TextField
                fullWidth
                id="billingZipCode"
                name="billingZipCode"
                placeholder="Enter billing zip code"
                value={formik.values.billingZipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingZipCode && Boolean(formik.errors.billingZipCode)}
                helperText={formik.touched.billingZipCode && formik.errors.billingZipCode}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {/* NOTES Section */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
                NOTES
              </Typography>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                multiline
                rows={6}
                placeholder="Enter any additional notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => formik.resetForm()}
                  disabled={saving}
                  sx={{ borderRadius: 0, px: 4 }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={saving}
                  sx={{ minWidth: 120, borderRadius: 0, px: 4, bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Projects
        </Typography>
        {formattedProjects.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No projects associated with this client yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Contract Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formattedProjects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>{project.name}</TableCell>
                    <TableCell>{project.description}</TableCell>
                    <TableCell align="right">
                      {project.contractAmount.toLocaleString(undefined, {
                        style: "currency",
                        currency: "USD",
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default ClientDetails;

